import { MouseEvent, useCallback, useMemo } from 'react';

import { CmsApiS3FileLink } from 'modules/cms/api/types/CmsApiLink';
import { showModal } from 'modules/main/store/ducks/modal';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { fileIdToUrl } from 'utils/s3file';

import { useIsLoggedIn } from './useIsLoggedIn';

type Props = {
  eventName: string;
  eventProperties: Record<string, unknown>;
  buttonData: CmsApiS3FileLink;
};

export function useCmsDownloadPermissions(props: Props) {
  const { buttonData, eventName, eventProperties } = props;

  const dispatch = useMainDispatch();
  const isLoggedIn = useIsLoggedIn();

  const allowDownload = useMemo(
    () => buttonData.data.permission === 'PUBLIC' || isLoggedIn,
    [buttonData, isLoggedIn],
  );

  const href = useMemo(
    () => (allowDownload ? fileIdToUrl(buttonData.data.s3FileId) : '#'),
    [allowDownload, buttonData.data.s3FileId],
  );

  const onClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (!allowDownload) {
        e.preventDefault();
        e.stopPropagation();
        const redirectTo = `${window.location.pathname}${window.location.search}`;
        dispatch(
          showModal('LOGIN', {
            redirectTo,
          }),
        );
      } else {
        trackClicked(eventName, {
          url: href,
          is_download_button: true,
          ...eventProperties,
        });
      }
    },
    [dispatch, href, allowDownload, eventName, eventProperties],
  );

  return { onClick, href, download: true };
}
